import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Calculator, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { validatePassword } from '@/lib/passwordValidation';
import { checkRateLimit, getRetryAfter } from '@/lib/rateLimiter';

type Step = 'loading' | 'signup' | 'nameOrg' | 'redeeming' | 'done' | 'error';

/** The three safe fields get_invite_preview exposes for a valid token. */
interface InvitePreview {
  type: 'new_org' | 'org_member' | string;
  org_name: string | null;
  email: string | null;
}

export default function Invite() {
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const redeemingRef = useRef(false);

  const [step, setStep] = useState<Step>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inviteType, setInviteType] = useState<string | null>(null);
  const [mode, setMode] = useState<'signup' | 'login'>('signup');
  const [orgName, setOrgName] = useState('');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  // Set when signup discovers the address already has an account (typically
  // one an admin created, which has no password yet). Drives the sign-in
  // wording and the "email me a password setup link" shortcut.
  const [existingAccount, setExistingAccount] = useState(false);
  const [sendingSetup, setSendingSetup] = useState(false);

  // Pre-signup peephole: learn the invite's type, the inviting org's name,
  // and the addressed email so the form can prefill before login exists.
  // Fails soft — if the RPC errors, the form still works without prefill.
  useEffect(() => {
    if (!token) { setPreviewLoaded(true); return; }
    supabase.rpc('get_invite_preview', { p_token: token })
      .then(({ data }) => {
        const p = data as InvitePreview | null;
        if (p) {
          setPreview(p);
          if (p.email) setEmail(p.email);
          // Joining: the org they join. New agency/team: the name whoever
          // added it chose. Either way it's locked.
          if (p.org_name) setOrgName(p.org_name);
        }
      })
      .then(() => setPreviewLoaded(true), () => setPreviewLoaded(true));
  }, [token]);

  const isNewOrgInvite = preview?.type === 'new_org';
  const isJoinInvite = preview?.type === 'org_member';
  // An agency or team arrives already named by whoever added it.
  const isNamedBranch = isNewOrgInvite && !!preview?.org_name;
  const orgNameLocked = isJoinInvite || isNamedBranch;

  const redeemInvite = async () => {
    if (!token || redeemingRef.current) return;

    redeemingRef.current = true;
    setStep('redeeming');

    try {
      const { data, error } = await supabase.rpc('redeem_invite', {
        invite_token: token,
        p_org_name: orgName.trim() || null,
      });
      if (error) throw error;

      const result = data as { error?: string; type?: string } | null;
      if (result?.error) {
        setErrorMsg(result.error);
        setStep('error');
        redeemingRef.current = false;
        return;
      }

      await supabase.auth.refreshSession();
      await refreshUserData();

      setInviteType(result?.type ?? null);
      setStep('done');

      setTimeout(() => {
        navigate('/', { replace: true });
      }, 500);
    } catch (err: any) {
      redeemingRef.current = false;
      setErrorMsg(err.message || 'Failed to redeem invite');
      setStep('error');
    }
  };

  useEffect(() => {
    if (authLoading || !previewLoaded) return;
    if (user) {
      // A new-org invite needs its org named before redemption — even for an
      // already-logged-in user, so don't auto-redeem into the required-name error.
      if (preview?.type === 'new_org' && !preview.org_name) {
        setStep('nameOrg');
        return;
      }
      void redeemInvite();
      return;
    }
    setStep('signup');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, token, previewLoaded]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isNewOrgInvite && !orgName.trim()) {
      toast.error('Organization name is required');
      return;
    }
    if (!checkRateLimit('auth:invite-signup', 3, 60_000)) {
      const wait = getRetryAfter('auth:invite-signup', 3, 60_000);
      toast.error('Too many attempts', { description: `Please wait ${wait} seconds before trying again.` });
      return;
    }
    const pw = validatePassword(password);
    if (!pw.valid) {
      toast.error('Weak password', { description: pw.errors.join('. ') });
      return;
    }
    setSubmitting(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName, pending_invite_token: token },
          emailRedirectTo: `${window.location.origin}/invite/${token}`,
        },
      });
      // The address already has an account. Supabase reports this two ways:
      // an explicit "already registered" error, or (with email-enumeration
      // protection on) a fake success whose user has no identities. Either
      // way the password just typed was NOT saved, so "check your email"
      // would be a lie — send them to sign in, with a way to set a password
      // when the account was created for them and never had one.
      const alreadyExists = /already registered/i.test(error?.message ?? '')
        || (!error && !!data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0);
      if (alreadyExists) {
        setExistingAccount(true);
        setMode('login');
        setPassword('');
        setSubmitting(false);
        toast.error('This email already has an account', {
          description: 'Sign in to accept the invite. Never set a password? Use the setup-link button below.',
          duration: 8000,
        });
        return;
      }
      if (error) throw error;

      const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
      if (loginError) {
        toast.success('Check your email', { description: 'Please confirm your email address, then click the invite link again.' });
        setSubmitting(false);
        return;
      }

      await redeemInvite();
    } catch (err: any) {
      toast.error('Error', { description: sanitizeError(err) });
      setSubmitting(false);
    }
  };

  // For an account that exists but has no password (admin-created) or a
  // forgotten one: same recovery email the sign-in page's Forgot password
  // sends. After setting it they land signed in; opening this invite link
  // again then redeems it automatically.
  const handleSendSetupLink = async () => {
    if (!email.trim()) { toast.error('Enter your email first'); return; }
    if (!checkRateLimit('auth:forgot', 3, 60_000)) {
      const wait = getRetryAfter('auth:forgot', 3, 60_000);
      toast.error('Too many attempts', { description: `Please wait ${wait} seconds before trying again.` });
      return;
    }
    setSendingSetup(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSendingSetup(false);
    if (error) {
      toast.error('Error', { description: sanitizeError(error) });
    } else {
      toast.success('Check your inbox', {
        description: 'Set your password from the link, then open this invite link again to join.',
        duration: 10000,
      });
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isNewOrgInvite && !orgName.trim()) {
      toast.error('Organization name is required');
      return;
    }
    setSubmitting(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      await redeemInvite();
    } catch (err: any) {
      toast.error('Login failed', { description: sanitizeError(err) });
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto h-12 w-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <Calculator className="h-6 w-6 text-primary-foreground" />
          </div>

          {step === 'loading' && (
            <>
              <CardTitle>Loading…</CardTitle>
              <CardDescription>Checking your invite</CardDescription>
            </>
          )}
          {step === 'signup' && (
            <>
              <CardTitle>{mode === 'signup' ? 'Accept Your Invitation' : 'Sign In to Continue'}</CardTitle>
              <CardDescription>
                {mode === 'signup'
                  ? 'Create your account to join Tactical Tax Genie'
                  : existingAccount
                    ? 'This email already has an account. Sign in to accept the invite.'
                    : 'Sign in with your existing account'}
              </CardDescription>
            </>
          )}
          {step === 'nameOrg' && (
            <>
              <CardTitle>Name Your Organization</CardTitle>
              <CardDescription>This is the name your team and network will see</CardDescription>
            </>
          )}
          {step === 'redeeming' && (
            <>
              <CardTitle>Setting up…</CardTitle>
              <CardDescription>Activating your invite</CardDescription>
            </>
          )}
          {step === 'done' && (
            <>
              <div className="mx-auto">
                <CheckCircle className="h-8 w-8 text-secondary" />
              </div>
              <CardTitle>You're in!</CardTitle>
              <CardDescription>
                {inviteType === 'new_org'
                  ? 'Redirecting to set up your account…'
                  : 'Redirecting to your dashboard…'}
              </CardDescription>
            </>
          )}
          {step === 'error' && (
            <>
              <div className="mx-auto">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle>Invite Error</CardTitle>
              <CardDescription className="text-destructive">{errorMsg}</CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent>
          {(step === 'loading' || step === 'redeeming') && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {step === 'signup' && (
            <>
              <form onSubmit={mode === 'signup' ? handleSignup : handleLogin} className="space-y-4">
                {mode === 'signup' && (
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      placeholder="Jane Smith"
                      required
                      className="h-11"
                    />
                  </div>
                )}
                {(isNewOrgInvite || (isJoinInvite && mode === 'signup')) && (
                  <div className="space-y-2">
                    <Label>Organization Name</Label>
                    <Input
                      value={orgName}
                      onChange={e => setOrgName(e.target.value)}
                      placeholder="Meridian Financial Group"
                      required={isNewOrgInvite}
                      readOnly={orgNameLocked}
                      className={`h-11 ${orgNameLocked ? 'bg-muted' : ''}`}
                    />
                    {isJoinInvite && (
                      <p className="text-xs text-muted-foreground">You're joining this organization.</p>
                    )}
                    {isNamedBranch && (
                      <p className="text-xs text-muted-foreground">You'll run this. The person who invited you named it.</p>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    readOnly={!!preview?.email}
                    className={`h-11 ${preview?.email ? 'bg-muted' : ''}`}
                  />
                  {!!preview?.email && (
                    <p className="text-xs text-muted-foreground">This invite is for this email address.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <PasswordInput
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={12}
                    className="h-11"
                  />
                  {mode === 'signup' && password.length > 0 && !validatePassword(password).valid && (
                    <p className="text-xs text-destructive mt-1">
                      {validatePassword(password).errors.join('. ')}
                    </p>
                  )}
                </div>
                <Button type="submit" className="w-full h-11" disabled={submitting}>
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Please wait…</>
                  ) : mode === 'signup' ? 'Create Account & Join' : 'Sign In & Join'}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-muted-foreground">
                {mode === 'signup' ? (
                  <p>
                    Already have an account?{' '}
                    <button type="button" className="underline hover:text-foreground font-medium" onClick={() => setMode('login')}>
                      Sign in
                    </button>
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p>
                      Forgot your password, or never set one?{' '}
                      <button type="button" className="underline hover:text-foreground font-medium" disabled={sendingSetup} onClick={handleSendSetupLink}>
                        {sendingSetup ? 'Sending…' : 'Email me a password setup link'}
                      </button>
                    </p>
                    {!existingAccount && (
                      <p>
                        Need an account?{' '}
                        <button type="button" className="underline hover:text-foreground font-medium" onClick={() => setMode('signup')}>
                          Create one
                        </button>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {step === 'nameOrg' && (
            <form
              onSubmit={e => {
                e.preventDefault();
                if (!orgName.trim()) { toast.error('Organization name is required'); return; }
                void redeemInvite();
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  placeholder="Meridian Financial Group"
                  required
                  className="h-11"
                />
              </div>
              <Button type="submit" className="w-full h-11">Create Organization & Continue</Button>
            </form>
          )}

          {step === 'error' && (
            <div className="flex flex-col gap-3 pt-2">
              {token && (
                <Button onClick={() => { setErrorMsg(''); redeemingRef.current = false; void redeemInvite(); }}>
                  Try Again
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/auth')}>Go to Login</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
